import classNames from 'classnames';
import { createContext, type ReactNode, useContext, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';

const splitQuery = '(min-width: 64rem)';

const subscribe = (onChange: () => void) => {
  if (typeof window.matchMedia !== 'function') {
    return () => null;
  }

  const query = window.matchMedia(splitQuery);
  query.addEventListener('change', onChange);

  return () => {
    query.removeEventListener('change', onChange);
  };
};

const getIsWide = () =>
  typeof window.matchMedia === 'function' && window.matchMedia(splitQuery).matches;

/** True from the width where the desktop layout splits into form column and brand panel. */
export const useIsWideDesktop = () => useSyncExternalStore(subscribe, getIsWide, () => false);

type Slots = {
  readonly nav?: HTMLElement;
  readonly footer?: HTMLElement;
};

export const ColumnFrameContext = createContext<Slots>({});

type Props = {
  readonly slot: keyof Slots;
  readonly children: ReactNode;
};

/**
 * Lifts page chrome (back / skip, legal links) into the split desktop column's top and bottom
 * rows. Anywhere else it renders in place, so mobile and the narrow desktop are unchanged.
 */
const ColumnFrame = ({ slot, children }: Props) => {
  const target = useContext(ColumnFrameContext)[slot];

  if (!target) {
    // eslint-disable-next-line react/jsx-no-useless-fragment -- keeps the return a JSX element, not a ReactNode that may be a promise
    return <>{children}</>;
  }

  // The footer container fades itself once (SplitLayout), so identical legal links don't
  // re-fade on every step.
  return createPortal(
    <div
      className={classNames(
        slot === 'nav' && 'absolute inset-0 animate-enter motion-reduce:animate-none'
      )}
    >
      {children}
    </div>,
    target
  );
};

export default ColumnFrame;
